import React from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';

import { Box, Display, Flex, Image, ImageProps } from '@rover/kibble/core';
import MQ from '@rover/kibble/styles/MediaQuery';
import LandingHero from '@rover/react-lib/src/pages/landing/components/LandingHero';
import LandingSearchForm, {
  Props as LandingSearchFormProps,
} from '@rover/react-lib/src/pages/landing/components/LandingSearchForm';
import {
  catSittingHeroImage,
  dogBoardingHeroImage,
  dogWalkingHeroImage,
  houseSittingHeroImage,
  petSittingHeroImage,
} from '@rover/react-lib/src/pages/landing/SeoLocation/images';
import { PetType } from '@rover/types/src/Pet';

// Either an explicit `heading` is supplied, or `serviceTypeName` + `locationName` are
// supplied so the component can build the default heading. Requiring one of the two
// prevents rendering an empty heading from missing data.
type HeadingProps =
  | { heading: React.ReactNode | string; serviceTypeName?: string; locationName?: string }
  | { heading?: undefined; serviceTypeName: string; locationName: string };

type Props = {
  desktopImage?: string;
  petType?: string | null;
  internalServiceTypeName?: string;
  subheading: string;
  showTrustpilot?: boolean;
} & HeadingProps &
  LandingSearchFormProps;

// Selects the hero image based on the pet type and the internal service type, mirroring the
// images the per-service SEO location pages use. Falls back to the dog boarding image.
export const getHeroImage = (
  petType: string | null | undefined,
  internalServiceTypeName?: string
): string => {
  if (petType === PetType.CAT) {
    return catSittingHeroImage;
  }
  switch (internalServiceTypeName) {
    case 'dog-walking':
      return dogWalkingHeroImage;
    case 'overnight-traveling':
      return houseSittingHeroImage;
    case 'drop-in':
      return petSittingHeroImage;
    default:
      return dogBoardingHeroImage;
  }
};

// styled image is required because kibble images don't support aspect-ratio yet
const HeroImage = styled(Image)`
  // we use aspect-ratio instead of using height auto to reduce CLS
  aspect-ratio: 1.21;
  // limit the image height to avoid it being the tallest thing in the Hero
  max-height: 400px;
`;

export const HeroImageWrapper: React.FC<ImageProps> = ({
  description,
  height,
  width,
  src,
  resizeMethod,
  webProps,
}) => {
  return (
    <>
      <Helmet>
        <link
          media={MQ.NOT_XS.getBreakpointString()}
          rel="preload"
          as="image"
          href={src as string}
          fetchPriority="high"
        />
      </Helmet>
      <HeroImage
        description={description}
        height={height}
        width={width}
        src={src}
        resizeMethod={resizeMethod}
        webProps={webProps}
      />
    </>
  );
};

const SeoLocationHero: React.FC<Props> = ({
  desktopImage,
  petType,
  internalServiceTypeName,
  serviceTypeName,
  locationName,
  heading,
  subheading,
  showTrustpilot = false,
  ...landingSearchFormProps
}) => {
  const heroImage = desktopImage ?? getHeroImage(petType, internalServiceTypeName);

  const heroHeading = heading ?? (
    <>
      {serviceTypeName}{' '}
      <Display webProps={{ as: 'span', responsive: false }} size="500" textColor="display">
        {locationName}
      </Display>
    </>
  );

  return (
    <Flex position="relative" pt="12x" pb="6x" justifyContent="center" alignItems="flexStart">
      <Flex
        width="100%"
        maxWidth="416px"
        justifyContent="center"
        flexDirection="column"
        flexBasis={['100%', '50%', '416px']}
      >
        <LandingHero.Display>{heroHeading}</LandingHero.Display>
        <LandingHero.Text>{subheading}</LandingHero.Text>
        <LandingSearchForm {...landingSearchFormProps} />
        {showTrustpilot && <LandingHero.TrustPilot />}
      </Flex>
      <Box display={['none', 'block']} flexBasis="50%">
        <HeroImageWrapper
          description=""
          height="auto"
          width="100%"
          src={heroImage}
          resizeMethod="contain"
          webProps={{
            eager: true,
            // @ts-expect-error fetchPriority is not in ImageWebOnlyProps, but is a valid img attribute
            fetchPriority: 'high',
          }}
        />
      </Box>
    </Flex>
  );
};

export default SeoLocationHero;
